import React, { useState, useEffect } from "react";
import {
  FiX,
  FiSearch,
  FiShield,
  FiRefreshCw,
  FiClock,
  FiSmartphone,
  FiUser,
  FiAlertTriangle,
  FiCheckCircle,
  FiMapPin,
  FiCpu,
} from "react-icons/fi";
import { format } from "date-fns";
import SkeletonTable from "./common/SkeletonTable";
import ClockingDeviceModal from "./ClockingDeviceModal";
import { usePaginatedFetch } from "../hooks/usePaginatedFetch";

const OUTCOME_LABELS = {
  success: {
    label: "Success",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: FiCheckCircle,
  },
  outside_geofence: {
    label: "Outside Geofence",
    color: "bg-amber-50 text-amber-800 border-amber-200",
    icon: FiMapPin,
  },
  unauthorised_device: {
    label: "Unauthorised Device",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: FiAlertTriangle,
  },
  no_authorised_device: {
    label: "No Station Configured",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: FiSmartphone,
  },
  invalid_credential: {
    label: "Invalid QR / Version",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: FiAlertTriangle,
  },
  duplicate_action: {
    label: "Duplicate Action",
    color: "bg-yellow-50 text-yellow-800 border-yellow-200",
    icon: FiClock,
  },
  staff_not_found: {
    label: "Staff Not Found",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: FiUser,
  },
  location_unavailable: {
    label: "Location Unavailable",
    color: "bg-slate-100 text-slate-700 border-slate-200",
    icon: FiMapPin,
  },
};

const METHOD_LABELS = {
  face: { label: "Face ID", color: "bg-blue-50 text-blue-700 border-blue-200" },
  qr: { label: "School QR", color: "bg-purple-50 text-purple-700 border-purple-200" },
  id_card: { label: "Staff ID Card", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const ClockAttemptsModal = ({ isOpen, onClose, school, refreshSchool }) => {
  const schoolId = school?._id;

  // Filters state
  const [selectedOutcome, setSelectedOutcome] = useState("blocked");
  const [selectedMethod, setSelectedMethod] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDeviceModal, setShowDeviceModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Backend treats outcome="all" as sentinel for all outcomes; "blocked" defaults to omitting the param
  const outcomeParam = selectedOutcome === "blocked" ? undefined : selectedOutcome;

  const {
    items: attempts,
    loading,
    error,
    page,
    setPage,
    limit,
    setLimit,
    totalDocuments,
    totalPages,
    refetch: fetchClockAttempts,
  } = usePaginatedFetch(schoolId ? `/staff-attendance/${schoolId}/clock-attempts` : null, {
    enabled: Boolean(isOpen && schoolId),
    initialLimit: 20,
    params: {
      method: selectedMethod === "all" ? undefined : selectedMethod,
      outcome: outcomeParam,
      search: debouncedSearch.trim() || undefined,
    },
    deps: [selectedMethod, selectedOutcome, debouncedSearch],
    mapResponse: (data) => ({
      items: data?.attempts || [],
      totalDocuments: data?.total_documents || 0,
      totalPages: data?.total_pages || 1,
    }),
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedOutcome, selectedMethod, debouncedSearch, setPage]);

  // Client-side fallback filter for search if backend doesn't filter by search param
  const filteredAttempts = attempts.filter((row) => {
    if (!debouncedSearch.trim()) return true;
    const term = debouncedSearch.toLowerCase();
    const staffName = (row.staff?.name || "").toLowerCase();
    const staffEmail = (row.staff?.email || "").toLowerCase();
    const outcomeMessage = (row.outcome_message || "").toLowerCase();
    const method = (row.method || "").toLowerCase();
    return (
      staffName.includes(term) ||
      staffEmail.includes(term) ||
      outcomeMessage.includes(term) ||
      method.includes(term)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-6 animate-fadeIn">
      <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] max-h-[850px] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-800">Staff Clock Attempts</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                {totalDocuments} {totalDocuments === 1 ? "Record" : "Records"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Audit trail of teacher clock-in and clock-out attempts, geofence validations, and
              device security
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDeviceModal(true)}
              className="inline-flex items-center gap-1.5 py-1.5 px-3.5 border border-[#3F3DA5]/30 shadow-sm text-xs font-semibold rounded-xl text-[#3F3DA5] bg-indigo-50/80 hover:bg-indigo-100 transition-colors"
              title="Manage clocking station device"
            >
              <FiSmartphone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Clocking Device</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
              title="Close modal"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toolbar & Filters */}
        <div className="px-6 py-3.5 bg-white border-b border-slate-100 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          {/* Search & Method / Outcome Dropdowns */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <div className="relative flex-1 sm:w-44 min-w-[140px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search staff, reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
            </div>

            <select
              value={selectedOutcome}
              onChange={(e) => setSelectedOutcome(e.target.value)}
              className="text-xs py-1.5 px-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 font-medium cursor-pointer"
              title="Filter by specific outcome"
            >
              <option value="blocked">Blocked / Failed</option>
              <option value="all">All Outcomes</option>
              <option value="outside_geofence">Outside Geofence</option>
              <option value="unauthorised_device">Unauthorised Device</option>
              <option value="no_authorised_device">No Station Configured</option>
              <option value="invalid_credential">Invalid QR / Version</option>
              <option value="duplicate_action">Duplicate Action</option>
              <option value="staff_not_found">Staff Not Found</option>
              <option value="location_unavailable">Location Unavailable</option>
              <option value="success">Success Only</option>
            </select>

            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className="text-xs py-1.5 px-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 font-medium cursor-pointer"
              title="Filter by clocking method"
            >
              <option value="all">All Methods</option>
              <option value="id_card">Staff ID Card</option>
              <option value="face">Face ID</option>
              <option value="qr">School QR</option>
            </select>

            <button
              onClick={fetchClockAttempts}
              disabled={loading}
              className="p-2 text-slate-500 hover:text-indigo-600 rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-50 flex-shrink-0"
              title="Refresh log"
            >
              <FiRefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin text-indigo-600" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="px-6 py-2.5 bg-rose-50 border-b border-rose-100 flex items-center gap-2 text-xs font-semibold text-rose-700">
            <FiAlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && !filteredAttempts.length ? (
            <div className="p-8">
              <SkeletonTable rows={6} columns={7} />
            </div>
          ) : !filteredAttempts.length ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                <FiShield className="w-6 h-6" />
              </div>
              <p className="text-sm font-semibold text-slate-700">No clock attempts found</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                {selectedOutcome === "blocked"
                  ? "Great news! There are no blocked or failed clock attempts for this period."
                  : "No matching clock attempts found for the selected filters."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-slate-100 bg-slate-50/90 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="py-3 pl-6 pr-3">Staff Member</th>
                  <th className="py-3 px-3">Action</th>
                  <th className="py-3 px-3">Method</th>
                  <th className="py-3 px-3">Outcome</th>
                  <th className="py-3 px-3">Reason / Details</th>
                  <th className="py-3 px-3">Device & Distance</th>
                  <th className="py-3 pr-6 pl-3 text-right">Attempted At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredAttempts.map((attempt) => {
                  const outcomeMeta = OUTCOME_LABELS[attempt.outcome] || {
                    label: attempt.outcome || "Unknown",
                    color: "bg-slate-100 text-slate-700 border-slate-200",
                    icon: FiShield,
                  };
                  const methodMeta = METHOD_LABELS[attempt.method] || {
                    label: attempt.method || "—",
                    color: "bg-slate-100 text-slate-700 border-slate-200",
                  };

                  const staffName = attempt.staff?.name || "Unknown Staff";
                  const staffEmail = attempt.staff?.email || "";
                  const initials = staffName
                    .split(" ")
                    .filter(Boolean)
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();

                  const deviceOs = attempt.device?.os || attempt.device?.platform || "";
                  const deviceModel = attempt.device?.model || "";
                  const deviceLabel = `${deviceOs} ${deviceModel}`.trim();

                  return (
                    <tr key={attempt._id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Staff Member */}
                      <td className="py-3 pl-6 pr-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                            {attempt.staff?.avatar ? (
                              <img
                                src={attempt.staff.avatar}
                                alt={staffName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-semibold text-slate-500 text-xs">
                                {initials || "ST"}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{staffName}</p>
                            {staffEmail && (
                              <p className="text-[11px] text-slate-400">{staffEmail}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                            attempt.clock_type === "in"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {attempt.clock_type === "in" ? "Clock In" : "Clock Out"}
                        </span>
                      </td>

                      {/* Method */}
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 text-[11px] font-semibold rounded-full border ${methodMeta.color}`}
                        >
                          {methodMeta.label}
                        </span>
                      </td>

                      {/* Outcome */}
                      <td className="py-3 px-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-semibold rounded-full border ${outcomeMeta.color}`}
                        >
                          <outcomeMeta.icon className="w-3 h-3 flex-shrink-0" />
                          <span>{outcomeMeta.label}</span>
                        </span>
                      </td>

                      {/* Reason / Details */}
                      <td className="py-3 px-3 max-w-[220px]">
                        <span
                          className="text-xs text-slate-600 truncate block"
                          title={attempt.outcome_message || "—"}
                        >
                          {attempt.outcome_message || "—"}
                        </span>
                      </td>

                      {/* Device & Distance */}
                      <td className="py-3 px-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-[11px] text-slate-700 font-medium">
                            <FiCpu className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span
                              className="truncate max-w-[130px] block"
                              title={attempt.device?.user_agent || deviceLabel || "—"}
                            >
                              {deviceLabel || "Detected Device"}
                            </span>
                          </div>
                          {attempt.distance_from_school !== undefined &&
                            attempt.distance_from_school !== null && (
                              <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
                                <FiMapPin className="w-2.5 h-2.5 text-slate-400 flex-shrink-0" />
                                <span>{Math.round(attempt.distance_from_school)}m from school</span>
                              </div>
                            )}
                        </div>
                      </td>

                      {/* Attempted At */}
                      <td className="py-3 pr-6 pl-3 text-right">
                        <span className="text-xs text-slate-500 whitespace-nowrap">
                          {attempt.attempted_at
                            ? format(new Date(attempt.attempted_at), "dd/MM/yyyy hh:mm a")
                            : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer & Pagination */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span>
              Showing{" "}
              <span className="font-semibold text-slate-700">{filteredAttempts.length}</span> of{" "}
              <span className="font-semibold text-slate-700">{totalDocuments}</span> attempts
            </span>
            <div className="flex items-center gap-1 text-[11px] text-slate-500">
              <span>Per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="py-0.5 px-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors shadow-sm"
            >
              Previous
            </button>
            <span className="px-2 font-medium">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors shadow-sm"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Clocking Station Device Modal */}
      <ClockingDeviceModal
        isOpen={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
        schoolId={schoolId}
        school={school}
        refreshSchool={refreshSchool}
      />
    </div>
  );
};

export default ClockAttemptsModal;
